'use client'

import React, { KeyboardEvent, useEffect, useState } from 'react'
import Link from 'next/link'
import { Box, IconButton, InputAdornment, Theme, Typography, useMediaQuery } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import { Participant } from 'src/interfaces/participant'
import { ChannelType } from 'src/interfaces/shopLink'
import { SeasonOrganization } from 'src/interfaces/seasonOrganization'
import { ParticipantController } from 'src/controllers'
import { logger } from 'src/lib'
import { Utils } from 'src/lib/utils'
import { GTM_EVENT } from 'src/interfaces/gtm'
import { useGtm, useOrganization, useShopLink, useVisit, useAvatar } from '@/hooks'
import { Organization } from '@/interfaces/organization'
import { TextField } from '@/components/ui'
import { useSearchParams } from 'next/navigation'
import { GatewayModal } from './modal'
import {
  Button,
  FindButton,
  Form,
  InputGrid,
  OrganizationAddress,
  OrganizationGrid,
  OrganizationId,
  OrganizationIdWrapper,
  OrganizationItem,
  OrganizationList,
  OrganizationName,
  OrganizationStateInput,
  SelectedOrganization,
  ShopButton,
  Subtitle,
  Title,
  OrganizationMainData,
  OrganizationMainDataLeftPart,
  DefaultParticipantCard,
  DefaultParticipantCardName,
} from './Gateway.styled'
import Grid from '@mui/material/Grid2'
import { Avatar } from '@/components/ui/Avatar'

const GuestGateway: React.FC = () => {
  const [organizationMidasId, setOrganizationMidasId] = useState<string>('')
  const [defaultParticipant, setDefaultParticipant] = useState<Participant>()
  const [isVisibleOrganizationLookup, setVisibilityOrganizationLookup] = useState<boolean>(false)
  const {
    states,
    selectedState,
    organizationName,
    organizationList,
    selectedOrganization,
    setSelectedOrganization,
    onChangeOrganizationName,
    onChangeState,
    buildOrganizationAddress,
    loadOrganizationByMidasId,
  } = useOrganization()
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('sm'))
  const { buildShopLink } = useShopLink()
  const searchParams = useSearchParams()
  const midasId = searchParams.get('organizationMidasId')
  const { register: registerVisit, visitorId } = useVisit()
  const { sendDataToGTM } = useGtm()
  const { getAvatarLink } = useAvatar()
  useEffect(() => {
    if (!midasId || !Utils.isNumber(midasId)) {
      return
    }

    setOrganizationMidasId(midasId)
    loadOrganizationByMidasId(parseInt(midasId, 10))
  }, [midasId])

  useEffect(() => {
    loadDefaultParticipant()
    if (selectedOrganization) {
      sendDataToGTM({ event: GTM_EVENT.GUEST_GATEWAY_PICK_ORG })
    }
  }, [selectedOrganization])

  useEffect(() => {
    if (defaultParticipant && visitorId) {
      registerVisit({
        url: window.location.href,
        participants: [defaultParticipant],
        midasId: selectedOrganization?.midasId,
      })
    }
  }, [defaultParticipant, visitorId])

  const loadDefaultParticipant = async () => {
    if (
      !selectedOrganization ||
      !selectedOrganization.seasonOrganizations ||
      selectedOrganization.seasonOrganizations.length === 0
    )
      return
    const seasonOrganizationId = selectedOrganization.seasonOrganizations[0].id
    const {
      result: [error, participant],
    } = await ParticipantController.default(seasonOrganizationId)
    if (error) {
      logger.error(error)
    }
    if (participant) {
      setDefaultParticipant(participant)
    }
  }

  const onClickOrganization = (organization: Organization) => {
    setSelectedOrganization(organization)
    setOrganizationMidasId(String(organization.midasId))
    setVisibilityOrganizationLookup(false)
  }

  const clearSelectedOrganization = () => {
    setSelectedOrganization(null)
    setOrganizationMidasId('')
  }

  const renderOrganizationList = () => {
    if (organizationList.length === 0) return null
    return (
      <OrganizationList>
        {organizationList.map((organization) => (
          <OrganizationItem key={organization.id} divider onClick={() => onClickOrganization(organization)}>
            <div>
              <OrganizationName variant="body1">{organization.name}</OrganizationName>
              <OrganizationAddress variant="body1">{buildOrganizationAddress(organization)}</OrganizationAddress>
            </div>
          </OrganizationItem>
        ))}
      </OrganizationList>
    )
  }

  const getSeason = (organization: Organization): string => {
    const seasonOrganization = getSeasonOrganization(organization)
    return seasonOrganization?.season?.name || ''
  }

  const getSeasonOrganization = (organization: Organization): SeasonOrganization | undefined =>
    organization.seasonOrganizations ? organization.seasonOrganizations[0] : undefined

  const getShopLink = () => {
    if (!selectedOrganization) return ''
    return buildShopLink({
      seasonName: getSeason(selectedOrganization),
      organization: selectedOrganization,
      seasonOrganization: getSeasonOrganization(selectedOrganization),
      participants: defaultParticipant ? [defaultParticipant] : [],
      channel: ChannelType.GATEWAY,
    })
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      loadOrganizationByMidasId(Number((event.target as { value?: string }).value))
      event.preventDefault()
    }
  }

  const onShopNowClick = () => {
    sendDataToGTM({ event: GTM_EVENT.GUEST_GATEWAY_SHOP_NOW })
  }

  if (isVisibleOrganizationLookup) {
    return (
      <GatewayModal isVisible>
        <Box position="relative" height="100%">
          <Title variant="h2">Organization ID Look Up</Title>
          {organizationList.length === 0 && !isMobile ? (
            <>
              <Subtitle variant="body2">Be sure it's the school in your town</Subtitle>
              <Subtitle variant="body2">Enter at least 2 characters of your school's name</Subtitle>
            </>
          ) : null}
          <InputGrid container spacing={4}>
            <Grid container spacing={4}>
              <Grid size={{ xs: 12, md: 6 }}>
                <OrganizationStateInput
                  id="search-state"
                  options={states}
                  getOptionLabel={(option) => option.name}
                  value={selectedState}
                  renderInput={({ inputProps, InputLabelProps, InputProps, ...rest }) => (
                    <TextField
                      {...rest}
                      slotProps={{ htmlInput: inputProps, inputLabel: InputLabelProps, input: InputProps }}
                      label="Organization's state"
                      variant="outlined"
                    />
                  )}
                  onChange={onChangeState}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  id="organization-name"
                  label="Organization's name"
                  value={organizationName}
                  variant="outlined"
                  onChange={onChangeOrganizationName}
                />
              </Grid>
            </Grid>
            <OrganizationGrid>{renderOrganizationList()}</OrganizationGrid>
          </InputGrid>
          <FindButton sx={{ mt: -9 }} edge="start" size="small" onClick={() => setVisibilityOrganizationLookup(false)}>
            Back
          </FindButton>
        </Box>
      </GatewayModal>
    )
  }

  const renderParticipantList = () => {
    if (!selectedOrganization) {
      return
    }
    const defaultParticipant = selectedOrganization.defaultParticipant
    if (!defaultParticipant) {
      return
    }
    if (!defaultParticipant.files?.length) {
      return
    }
    return (
      <DefaultParticipantCard>
        <Avatar hasShadow size="third" src={getAvatarLink(defaultParticipant)} />
        <DefaultParticipantCardName noWrap variant="body1">
          {selectedOrganization.name}
        </DefaultParticipantCardName>
      </DefaultParticipantCard>
    )
  }

  const renderGoal = () => {
    if (!selectedOrganization) return null
    const seasonOrganization = getSeasonOrganization(selectedOrganization)

    if (!seasonOrganization || (!seasonOrganization.goal && !+seasonOrganization?.goalAmount)) return null
    return (
      <>
        {!!seasonOrganization.goalAmount && (
          <Box mt={1}>
            <Typography variant="h3">
              OUR TOTAL SALES GOAL{' '}
              {!!+seasonOrganization.goalAmount && Utils.formatMoney(seasonOrganization.goalAmount)}
            </Typography>
          </Box>
        )}
        {!!seasonOrganization.goal && (
          <Box mt={1}>
            <Typography
              variant="body2"
              dangerouslySetInnerHTML={{
                __html: Utils.textToHTML(seasonOrganization.goal),
              }}
            />
          </Box>
        )}
      </>
    )
  }

  return (
    <GatewayModal isVisible>
      <Form>
        <Title variant="h2">Enter the organization code</Title>
        <Subtitle variant="body2">Shopping to support an organization only</Subtitle>
        <OrganizationIdWrapper>
          <TextField
            fullWidth
            name="organizationMidasId"
            value={organizationMidasId}
            label="Organization ID"
            variant="outlined"
            slotProps={{
              input: {
                endAdornment: selectedOrganization ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="toggle password visibility"
                      edge="end"
                      onClick={clearSelectedOrganization}
                    >
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              },
            }}
            onChange={(event) => setOrganizationMidasId(event.target.value)}
            onBlur={(event) => loadOrganizationByMidasId(Number(event.target.value))}
            onKeyDown={handleKeyDown}
          />
        </OrganizationIdWrapper>

        {!selectedOrganization ? (
          <Box>
            <FindButton edge="start" size="small" onClick={() => setVisibilityOrganizationLookup(true)}>
              <SearchIcon />
              Find organization ID.
            </FindButton>
          </Box>
        ) : (
          <>
            <SelectedOrganization>
              <OrganizationMainData>
                <OrganizationMainDataLeftPart>
                  <OrganizationId>Organization ID {selectedOrganization.midasId}</OrganizationId>
                  <OrganizationAddress>{selectedOrganization.name}</OrganizationAddress>
                  <OrganizationAddress>
                    {buildOrganizationAddress(selectedOrganization, ['city', 'state'])}
                  </OrganizationAddress>
                </OrganizationMainDataLeftPart>
                {renderParticipantList()}
              </OrganizationMainData>

              {renderGoal()}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <ShopButton
                  fullWidth
                  LinkComponent={Link}
                  href={getShopLink()}
                  variant="contained"
                  onClick={onShopNowClick}
                >
                  Shop!
                </ShopButton>
              </Box>
            </SelectedOrganization>
          </>
        )}
      </Form>
    </GatewayModal>
  )
}

export default GuestGateway
